import { useEffect, useState } from 'react';

// Shared UI atoms for Privacy Meter

// ─── Small category chip ───
export function CategoryChip({ label, active = false, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: active ? '1px solid var(--ink)' : '1px solid var(--line)',
        background: active ? 'var(--ink)' : 'var(--paper)',
        color: active ? 'var(--paper)' : 'var(--ink-soft)',
        padding: '6px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: 'inherit',
        cursor: 'pointer',
        transition: 'all 140ms ease',
        flexShrink: 0,
      }}
    >
      {label}
    </button>
  );
}

// ─── Risk tier badge (F4 hotspot vocabulary: low / med / high) ───
// Shared by the F4 message bubble and the fixed D5 certainty level
// (Risk Meter, hotspot tooltip, Mirror, F2 hard-stop sheet). `level` is a
// 1..3 risk tier (see severityToRiskTier); level 0 renders nothing.
// Each tier carries its own icon (severity iconography: x-octagon /
// alert-triangle / info-circle) so tiers are distinguishable by shape,
// not color alone.
export function RiskBadge({ level }) {
  if (level === 0) return null;
  const cfg = {
    1: { bg: 'var(--risk-1-bg)', fg: 'var(--risk-1-text)', label: 'low', icon: Icons.info },
    2: {
      bg: 'var(--risk-2-bg)',
      fg: 'var(--risk-2-text)',
      label: 'med',
      icon: Icons.alertTriangle,
    },
    3: { bg: 'var(--risk-3-bg)', fg: 'var(--risk-3-text)', label: 'high', icon: Icons.xOctagon },
  }[level];
  return (
    <span
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 9,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        padding: '2px 6px',
        borderRadius: 4,
        background: cfg.bg,
        color: cfg.fg,
      }}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0 }}>{cfg.icon(10, cfg.fg)}</span>
      {cfg.label}
    </span>
  );
}

// ─── Reveal notification dot ───
// Marks a feature entry point (Mirror button, Risk bar, Hotspots toggle)
// that appeared mid-experiment (see backend.study.featureRevealDelayMinutes /
// app.jsx's revealDelayMs) until the participant first opens/toggles it.
// Purely decorative — the parent control already carries its own
// aria-label, so this stays out of the accessible name via aria-hidden.
// Position the parent `relative` so this absolutely-positioned dot lands
// at its top-right corner.
export function NotificationDot() {
  return <span className="pm-notify-dot" data-testid="notify-dot" aria-hidden="true" />;
}

// ─── Risk legend (High / Med / Low key for the fixed D5 level) ───
// Mirrors the researcher-panel hotspot legend swatch pattern: a
// --risk-N-bg fill with a --risk-N-edge border, followed by the label.
export function RiskLegend() {
  const tiers = [
    { n: 3, label: 'High' },
    { n: 2, label: 'Med' },
    { n: 1, label: 'Low' },
  ];
  return (
    <div
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 10,
        color: 'var(--ink-soft)',
        letterSpacing: '0.04em',
      }}
    >
      {tiers.map((t) => (
        <span key={t.n} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 10,
              height: 6,
              borderRadius: 2,
              background: `var(--risk-${t.n}-bg)`,
              border: `1px solid var(--risk-${t.n}-edge)`,
            }}
          />
          {t.label}
        </span>
      ))}
    </div>
  );
}

// ─── Shared pending-analysis copy ───
export const ANALYZING_COPY = 'Analyzing your conversation…';

// ─── Inference Freshness ───
export function InferenceFreshness({ triggerTime, className, style }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!triggerTime) return undefined;
    setNow(Date.now());
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [triggerTime]);

  if (!triggerTime) return null;
  const seconds = Math.max(0, Math.floor((now - triggerTime) / 1000));

  return (
    <span
      className={`mono ${className ?? ''}`}
      style={{
        fontSize: 11,
        color: 'var(--ink-muted)',
        ...style,
      }}
    >
      Last refresh: {seconds}s before
    </span>
  );
}

// ─── Icon set (hand-rolled, simple) ───
export const Icons = {
  shield: (size = 18, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3l8 3v6c0 5-3.5 8.5-8 9.5-4.5-1-8-4.5-8-9.5V6l8-3z"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  back: (size = 20, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M15 5l-7 7 7 7"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  close: (size = 18, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6L6 18" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  send: (size = 20, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M3 11.5L21 4l-7.5 17-2.3-7.5L3 11.5z"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  plus: (size = 18, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M5 12h14" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  info: (size = 14, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" data-icon="info">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6" />
      <path d="M12 11v5M12 8v.01" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  // Severity iconography for the risk tiers (RiskBadge): the three
  // shapes must stay distinguishable at ~10px, so strokes are slightly
  // heavier than the chrome icons above.
  alertTriangle: (size = 14, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" data-icon="alert-triangle">
      <path
        d="M12 3.5L21.5 20h-19L12 3.5z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M12 10v4.5M12 17.5v.01" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  xOctagon: (size = 14, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" data-icon="x-octagon">
      <path
        d="M8.2 3h7.6L21 8.2v7.6L15.8 21H8.2L3 15.8V8.2L8.2 3z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ),
  chevron: (size = 14, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M9 6l6 6-6 6"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  expand: (size = 14, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M4 10V4h6M20 14v6h-6"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  eye: (size = 16, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.6" />
    </svg>
  ),
  // Speedometer/dial — the F3 risk meter's own glyph (reads as "a meter",
  // pairs with the "RISK" eyebrow). Needle points up-right toward "high".
  gauge: (size = 16, color = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" data-icon="gauge">
      <path d="M4 18a8 8 0 0 1 16 0" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M12 18l4.2-5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="18" r="1.5" fill={color} />
    </svg>
  ),
};
